/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Italic } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import {
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	PictureEditing,
	AutoImage,
	ImageInsert,
	ImageResize
} from '@ckeditor/ckeditor5-image';
import { Indent } from '@ckeditor/ckeditor5-indent';
import { Link, AutoLink, LinkImage } from '@ckeditor/ckeditor5-link';
import { List } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import {
	Table,
	TableToolbar,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties
} from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily.js';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize.js';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting.js';
import {
	Strikethrough,
	Subscript,
	Superscript,
	Underline,
	Code
} from '@ckeditor/ckeditor5-basic-styles/src';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import {
	SpecialCharacters,
	SpecialCharactersEssentials
} from '@ckeditor/ckeditor5-special-characters';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { EasyImage } from '@ckeditor/ckeditor5-easy-image';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
import { ExportPdf } from '@ckeditor/ckeditor5-export-pdf';

import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { Markdown } from '@ckeditor/ckeditor5-markdown-gfm';

class Editor extends ClassicEditor {
	public static override builtinPlugins = [
		Alignment,
		Autoformat,
		BlockQuote,
		Bold,
		CloudServices,
		Essentials,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		Heading,
		Image,
		ImageCaption,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		Italic,
		List,
		MediaEmbed,
		Paragraph,
		PasteFromOffice,
		SourceEditing,
		Table,
		TableToolbar,
		TextTransformation,
		Strikethrough,
		Subscript,
		Superscript,
		Underline,
		Code,
		CodeBlock,
		HorizontalLine,
		SpecialCharacters,
		SpecialCharactersEssentials,
		RemoveFormat,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		Link,
		PictureEditing,
		AutoImage,
		ImageInsert,
		ImageResize,
		AutoLink,
		LinkImage,
		EasyImage,
		WordCount,
		HtmlEmbed,
		ExportPdf
	] as any;

	public static override defaultConfig = {
		toolbar: {
			items: [
				'undo',
				'redo',
				'|',
				'heading',
				'|',
				'fontfamily',
				'fontsize',
				'fontColor',
				'fontBackgroundColor',
				'|',
				'bold',
				'italic',
				'underline',
				'strikethrough',
				'subscript',
				'superscript',
				'code',
				'codeBlock',
				'-',
				'|',
				'alignment',
				'link',
				'insertImage',
				'insertTable',
				'blockQuote',
				'|',
				'specialCharacters',
				'horizontalLine',
				'|',
				'bulletedList',
				'numberedList',
				'outdent',
				'indent',
				'|',
				'sourceEditing',
				'|',
				'removeFormat'
			]
		},
		language: 'en',
		codeBlock: {
			languages: [
				{ language: 'plaintext', label: 'Plain text' },
				{ language: 'typescript', label: 'TypeScript' },
				{ language: 'javascript', label: 'JavaScript' },
				{ language: 'html', label: 'HTML' },
				{ language: 'css', label: 'CSS' },
				{ language: 'json', label: 'JSON' },
				{ language: 'bash', label: 'Bash' }
			]
		},
		image: {
			toolbar: [
				'imageTextAlternative',
				'toggleImageCaption',
				'imageStyle:inline',
				'imageStyle:block',
				'imageStyle:side'
			]
		},
		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells',
				'tableProperties',
				'tableCellProperties'
			]
		}
	};
}

class MarkdownEditor extends ClassicEditor {
	public static override builtinPlugins = [
		Markdown,
		...Editor.builtinPlugins
	] as any;

	public static override defaultConfig = {
		...Editor.defaultConfig
	};
}

(globalThis as any).ClassicEditor = Editor;
(globalThis as any).MarkdownEditor = MarkdownEditor;

export default Editor;